import * as THREE from "three";
import { Character } from "./types";

interface CreateCharacterOptions {
  color?: string;
  isHuman?: boolean;
  label?: string;
  totalSlots?: number;
}

export function createCharacter(
  index: number,
  scene: THREE.Scene,
  options: CreateCharacterOptions = {}
): Character {
  const { color, isHuman = false, label, totalSlots = 8 } = options;
  // Create a group to hold all character parts
  const group = new THREE.Group();

  // Calculate position in a circle
  const radius = 8;
  const angle = (index / Math.max(totalSlots, 1)) * Math.PI * 2;
  const x = Math.cos(angle) * radius;
  const z = Math.sin(angle) * radius;

  // Set position
  const position = new THREE.Vector3(x, 0, z);
  group.position.copy(position);

  // Make character face the center
  group.lookAt(new THREE.Vector3(0, 0, 0));

  // Create character body
  const bodyGeometry = new THREE.CapsuleGeometry(0.5, 1, 4, 8);
  const bodyMaterial = new THREE.MeshStandardMaterial({
    color: 0xf5deb3, // Wheat color for body
    roughness: 0.7,
    metalness: 0.3,
  });
  const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
  body.position.y = 1.25;
  body.castShadow = true;
  group.add(body);

  // Create character head
  const headGeometry = new THREE.SphereGeometry(0.4, 16, 16);
  const headMaterial = new THREE.MeshStandardMaterial({
    color: 0xf5deb3, // Wheat color for head
    roughness: 0.7,
    metalness: 0.3,
  });
  const head = new THREE.Mesh(headGeometry, headMaterial);
  head.position.y = 2.2;
  head.castShadow = true;
  group.add(head);

  // Create character clothes (indicates role, only visible to player for debugging)
  const clothesGeometry = new THREE.CylinderGeometry(0.6, 0.6, 1, 8);
  const clothesMaterial = new THREE.MeshStandardMaterial({
    color: new THREE.Color(color ?? "#3498db"),
    roughness: 0.8,
    metalness: 0.2,
  });
  const clothes = new THREE.Mesh(clothesGeometry, clothesMaterial);
  clothes.position.y = 1.25;
  clothes.scale.set(0.9, 0.9, 0.9);
  clothes.castShadow = true;
  group.add(clothes);

  // Add player number above character
  const playerLabel = label ?? `P${index + 1}`;

  // Create canvas for the text
  const canvas = document.createElement("canvas");
  canvas.width = 200;
  canvas.height = 80;
  const context = canvas.getContext("2d");
  if (context) {
    context.fillStyle = "rgba(0, 0, 0, 0)";
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Text styling
    context.font = "bold 48px Arial";
    context.textAlign = "center";
    context.textBaseline = "middle";

    // Draw text
    context.fillStyle = isHuman ? "#FF9900" : "white";
    context.fillText(playerLabel, canvas.width / 2, canvas.height / 2);
  }

  // Create texture from canvas
  const texture = new THREE.CanvasTexture(canvas);

  // Create sprite material with the texture
  const spriteMaterial = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
  });
  spriteMaterial.color = new THREE.Color(isHuman ? "#FF9900" : "#ffffff");

  // Create sprite and position it above the character
  const sprite = new THREE.Sprite(spriteMaterial);
  sprite.position.y = 3.0;
  sprite.scale.set(1.5, 0.75, 1);
  group.add(sprite);

  let ring: THREE.Mesh | null = null;

  // If this is the player's character, add a marker
  if (isHuman) {
    // Add a glowing ring around the player's character
    const ringGeometry = new THREE.TorusGeometry(0.8, 0.05, 16, 32);
    const ringMaterial = new THREE.MeshStandardMaterial({
      color: 0xff9900, // Orange
      emissive: 0xff9900,
      emissiveIntensity: 2,
    });
    ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.position.y = 0.05; // Just above the ground
    ring.rotation.x = Math.PI / 2; // Lay flat
    group.add(ring);
  }

  // Add character to scene
  scene.add(group);

  // Create a simple animation for the character
  let animationTime = Math.random() * Math.PI * 2; // Random start time

  // Return the character object
  return {
    model: group,
    position,
    update: () => {
      // Simple idle animation
      animationTime += 0.01;

      // Subtle bobbing motion
      group.position.y = Math.sin(animationTime) * 0.1;

      // Subtle rotation
      group.rotation.y =
        Math.sin(animationTime * 0.5) * 0.1 + group.rotation.y * 0.99;
    },
    setColor: (hex: string) => {
      clothesMaterial.color.set(hex);
    },
    setAlive: (alive: boolean) => {
      if (alive) {
        clothesMaterial.transparent = false;
        clothesMaterial.opacity = 1;
      } else {
        clothesMaterial.transparent = true;
        clothesMaterial.opacity = 0.6;
      }
      if (ring) {
        ring.visible = alive;
      }
    },
    setHuman: (isHumanFlag: boolean) => {
      if (isHumanFlag) {
        if (!ring) {
          const ringGeometry = new THREE.TorusGeometry(0.8, 0.05, 16, 32);
          const ringMaterial = new THREE.MeshStandardMaterial({
            color: 0xff9900,
            emissive: 0xff9900,
            emissiveIntensity: 2,
          });
          ring = new THREE.Mesh(ringGeometry, ringMaterial);
          ring.position.y = 0.05;
          ring.rotation.x = Math.PI / 2;
          group.add(ring);
        }
        spriteMaterial.color.set("#FF9900");
      } else if (ring) {
        group.remove(ring);
        ring.geometry.dispose();
        if (!Array.isArray(ring.material)) {
          ring.material.dispose();
        }
        ring = null;
        spriteMaterial.color.set("#ffffff");
      } else {
        spriteMaterial.color.set("#ffffff");
      }
    },
  };
}
